/**
 * SourceAttribution.tsx — MQL → SQL → Closed funnel by source.
 * Route: /#/reports/source-attribution
 *
 * Track D patch (2026-05-13):
 *   - DEMO_ROWS (6 hardcoded channel rows) removed from real tenant paths
 *   - "2026 annual target: 600 MQLs per Bryan's 2026 specification" removed from page text
 *   - marketing_attribution schema does not exist — showing honest empty state
 *   - Demo tenant (tenant_id=2) continues to show seed data
 *
 * Palette: Mi6 black #0B0B0F · red #DC2626.
 */
import { fmtUSD } from "@/lib/reporting-live";
import OperatorShell from "@/components/operator/OperatorShell";
import { getCurrentTenant } from "@/lib/session";
import { parseDrillParams } from "@/lib/drillUrl";
import DrillDownFilterBar from "@/components/DrillDownFilterBar";

const MI6_BLACK  = "#0B0B0F";
const MI6_RED   = "#DC2626";
const MI6_SLATE = "#6B6B70";
const MI6_GREEN = "#059669";

interface SourceRow {
  source: string;
  mqls: number;
  sqls: number;
  opps: number;
  closed: number;
  revenue: number;
  mqlToSql: number;
  sqlToOpp: number;
  oppToClose: number;
}

// Demo seed data — shown only for tenant_id=2 (Mi6 Demo)
const DEMO_ROWS: SourceRow[] = [
  {
    source: "Trade Shows",
    mqls: 96, sqls: 38, opps: 18, closed: 6,
    revenue: 420_000,
    mqlToSql: 40, sqlToOpp: 47, oppToClose: 33,
  },
  {
    source: "Email Campaigns",
    mqls: 180, sqls: 54, opps: 22, closed: 8,
    revenue: 340_000,
    mqlToSql: 30, sqlToOpp: 41, oppToClose: 36,
  },
  {
    source: "Webinars",
    mqls: 120, sqls: 42, opps: 16, closed: 5,
    revenue: 215_000,
    mqlToSql: 35, sqlToOpp: 38, oppToClose: 31,
  },
  {
    source: "Hosted Round Tables",
    mqls: 48, sqls: 22, opps: 12, closed: 5,
    revenue: 380_000,
    mqlToSql: 46, sqlToOpp: 55, oppToClose: 42,
  },
  {
    source: "Paid Digital",
    mqls: 96, sqls: 24, opps: 9, closed: 2,
    revenue: 95_000,
    mqlToSql: 25, sqlToOpp: 38, oppToClose: 22,
  },
  {
    source: "Cold Outbound",
    mqls: 60, sqls: 18, opps: 8, closed: 3,
    revenue: 165_000,
    mqlToSql: 30, sqlToOpp: 44, oppToClose: 38,
  },
];

function pct(n: number, d: number): string {
  if (!d) return "—";
  return `${Math.round((n / d) * 100)}%`;
}

function FunnelBar({ value, max, color }: { value: number; max: number; color: string }) {
  const w = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${w}%`, background: color }}
        />
      </div>
      <span className="text-xs tabular-nums font-semibold w-8 text-right" style={{ color: MI6_BLACK }}>
        {value}
      </span>
    </div>
  );
}

function FunnelTable({ rows }: { rows: SourceRow[] }) {
  const totalMqls = rows.reduce((s, r) => s + r.mqls, 0);
  const totalSqls = rows.reduce((s, r) => s + r.sqls, 0);
  const totalOpps = rows.reduce((s, r) => s + r.opps, 0);
  const totalClosed = rows.reduce((s, r) => s + r.closed, 0);
  const totalRevenue = rows.reduce((s, r) => s + r.revenue, 0);
  const maxMqls = Math.max(...rows.map((r) => r.mqls));

  return (
    <>
      {/* Summary KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {[
          { label: "Total MQLs", value: totalMqls.toString(), color: MI6_BLACK },
          { label: "SQLs", value: totalSqls.toString(), sub: pct(totalSqls, totalMqls) + " of MQLs", color: MI6_BLACK },
          { label: "Opportunities", value: totalOpps.toString(), sub: pct(totalOpps, totalSqls) + " of SQLs", color: MI6_BLACK },
          { label: "Closed Won", value: totalClosed.toString(), sub: pct(totalClosed, totalOpps) + " of opps", color: MI6_GREEN },
          { label: "Revenue", value: fmtUSD(totalRevenue), sub: "from marketing-sourced", color: MI6_BLACK },
        ].map((k) => (
          <div
            key={k.label}
            className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4 border-l-4"
            style={{ borderLeftColor: k.color }}
          >
            <div className="text-[9px] uppercase tracking-[0.18em] font-bold mb-1" style={{ color: MI6_SLATE }}>
              {k.label}
            </div>
            <div className="text-xl font-extrabold tabular-nums" style={{ color: k.color }}>
              {k.value}
            </div>
            {k.sub && (
              <div className="text-xs mt-0.5" style={{ color: MI6_SLATE }}>{k.sub}</div>
            )}
          </div>
        ))}
      </div>

      {/* Funnel waterfall */}
      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden mb-6">
        <div className="px-5 pt-5 pb-3 border-b border-neutral-100">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: MI6_SLATE }}>
            Conversion by source channel
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                {["Source", "MQLs", "SQLs", "MQL→SQL", "Opps", "SQL→Opp", "Closed", "Opp→Close", "Revenue"].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left font-semibold" style={{ color: MI6_SLATE }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.source} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="px-4 py-3 font-semibold" style={{ color: MI6_BLACK }}>{r.source}</td>
                  <td className="px-4 py-3"><FunnelBar value={r.mqls} max={maxMqls} color={MI6_BLACK} /></td>
                  <td className="px-4 py-3 tabular-nums" style={{ color: MI6_BLACK }}>{r.sqls}</td>
                  <td className="px-4 py-3 tabular-nums font-semibold" style={{ color: r.mqlToSql >= 40 ? MI6_GREEN : r.mqlToSql >= 25 ? "#CA8A04" : MI6_RED }}>{r.mqlToSql}%</td>
                  <td className="px-4 py-3 tabular-nums" style={{ color: MI6_BLACK }}>{r.opps}</td>
                  <td className="px-4 py-3 tabular-nums font-semibold" style={{ color: r.sqlToOpp >= 45 ? MI6_GREEN : r.sqlToOpp >= 30 ? "#CA8A04" : MI6_RED }}>{r.sqlToOpp}%</td>
                  <td className="px-4 py-3 tabular-nums font-bold" style={{ color: MI6_GREEN }}>{r.closed}</td>
                  <td className="px-4 py-3 tabular-nums font-semibold" style={{ color: r.oppToClose >= 35 ? MI6_GREEN : r.oppToClose >= 25 ? "#CA8A04" : MI6_RED }}>{r.oppToClose}%</td>
                  <td className="px-4 py-3 tabular-nums font-semibold" style={{ color: MI6_BLACK }}>{fmtUSD(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-neutral-50 border-t border-neutral-200">
              <tr>
                <td className="px-4 py-3 font-bold" style={{ color: MI6_BLACK }}>Total</td>
                <td className="px-4 py-3 font-bold tabular-nums" style={{ color: MI6_BLACK }}>{totalMqls}</td>
                <td className="px-4 py-3 font-bold tabular-nums" style={{ color: MI6_BLACK }}>{totalSqls}</td>
                <td className="px-4 py-3 font-bold tabular-nums" style={{ color: MI6_BLACK }}>{pct(totalSqls, totalMqls)}</td>
                <td className="px-4 py-3 font-bold tabular-nums" style={{ color: MI6_BLACK }}>{totalOpps}</td>
                <td className="px-4 py-3 font-bold tabular-nums" style={{ color: MI6_BLACK }}>{pct(totalOpps, totalSqls)}</td>
                <td className="px-4 py-3 font-bold tabular-nums" style={{ color: MI6_GREEN }}>{totalClosed}</td>
                <td className="px-4 py-3 font-bold tabular-nums" style={{ color: MI6_BLACK }}>{pct(totalClosed, totalOpps)}</td>
                <td className="px-4 py-3 font-bold tabular-nums" style={{ color: MI6_BLACK }}>{fmtUSD(totalRevenue)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-10 text-center mb-6">
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: "#92400E",
          background: "#FEF3C7",
          border: "1px solid #FDE68A",
          borderRadius: 8,
          padding: "12px 24px",
          display: "inline-block",
          marginBottom: 16,
        }}
      >
        Marketing attribution wiring in progress
      </div>
      <p className="text-sm mt-2" style={{ color: MI6_SLATE }}>
        Source attribution requires the marketing_attribution schema.<br />
        Data activates as your team logs activity.
      </p>
    </div>
  );
}

export default function SourceAttribution() {
  const tenant = getCurrentTenant();
  const isDemo = tenant?.is_demo === true;

  // BUILD-14: read drill-down filter params
  const drillParams = parseDrillParams();
  const filterChannel = drillParams.channel ?? null;
  const backHref = (drillParams as any).from ?? "/dashboards/executive";
  const backLabel = (drillParams as any).fromLabel ?? "Executive Dashboard";

  return (
    <OperatorShell breadcrumbs={[{ label: "Source Attribution" }]} pageTitle="Source Attribution" pageMeta="Reports">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 flex-1 w-full">
        {/* BUILD-14: drill-down filter breadcrumb when arriving from dashboard */}
        {filterChannel ? (
          <DrillDownFilterBar
            backLabel={backLabel}
            backHref={backHref}
            filters={{ Channel: filterChannel }}
            onClear={() => { window.location.hash = "/reports/source-attribution"; }}
          />
        ) : (
          <a
            href={`#${backHref}`}
            className="text-xs font-medium"
            style={{ color: MI6_SLATE }}
          >
            ← {backLabel}
          </a>
        )}

        <div className="mt-4 mb-6">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: MI6_SLATE }}>
            Mi6 Attilius · Reports
          </div>
          <h1 className="text-2xl font-extrabold" style={{ color: MI6_BLACK }}>
            Source Attribution — MQL to Closed
          </h1>
          {isDemo && (
            <p className="text-sm mt-1" style={{ color: MI6_SLATE }}>
              Sample data · Checkpoint-to-close conversion by source channel
            </p>
          )}
        </div>

        {isDemo ? <FunnelTable rows={filterChannel ? DEMO_ROWS.filter((r) => r.source.toLowerCase().includes(filterChannel.toLowerCase()) || filterChannel.toLowerCase().includes(r.source.toLowerCase())) : DEMO_ROWS} /> : <EmptyState />}

      </main>
    </OperatorShell>
  );
}
